import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
  Stack,
} from '@hurdl/design-system';
import { Link, useParams } from '@tanstack/react-router';
import { useEffect, useState, type ReactElement } from 'react';

import { sizeSuffix } from '../bespoke/menu-workspace/review-editor.js';
import { type ReviewedItem } from '../bespoke/menu-workspace/types.js';
import { restaurantLabelOf, useMenuOperations } from './menus-wiring.js';
import { TILE_LINK_CLASS } from './tile.js';

/**
 * One Restaurant's reviewed menu (menus-spec R16): the approved items as a responsive
 * grid of cards, each opening the full-page editor.
 *
 * Reads the Restaurant key from the route, loads its reviewed items, and shows each as a
 * card with its image (a thumbnail where one is held, a placeholder where none is), its
 * name (truncated) and its category as a `Badge`. Choosing a card navigates to the
 * item's edit page — the same editor the review flow uses, as a page rather than a
 * modal, so the source-PDF control stays reachable while the form is on screen.
 * Placeholders stand in while the read is in flight, and an empty menu says so.
 *
 * The one `h1` is the page's; the `main` landmark is the Layout's. The cards' links
 * resolve against the route tree, so no address literal reaches this source.
 */
export function RestaurantMenuPage(): ReactElement {
  const { productKey, restaurantKey } = useParams({ strict: false });
  const key = restaurantKey ?? '';
  const product = productKey ?? '';
  const operations = useMenuOperations();

  const [items, setItems] = useState<ReadonlyArray<ReviewedItem> | undefined>(
    undefined,
  );

  useEffect(() => {
    let live = true;
    void operations.listReviewed(key).then((next) => {
      if (live) setItems(next);
    });
    return () => {
      live = false;
    };
  }, [operations, key]);

  return (
    <Stack gap="large">
      <Stack gap="small">
        <h1 className="min-w-0 truncate font-hurdl-display text-hurdl-heading font-hurdl-semibold">
          {restaurantLabelOf(key)} menu
        </h1>
        <p className="text-hurdl-body text-muted-foreground">
          The approved, published items. Choose one to edit its display,
          allergens, nutrition or image.
        </p>
      </Stack>

      {items === undefined ? (
        <div
          className="grid gap-hurdl-6 sm:grid-cols-2 xl:grid-cols-3"
          aria-busy="true"
        >
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : items.length === 0 ? (
        <p className="text-muted-foreground">
          No reviewed items for this restaurant yet.
        </p>
      ) : (
        <div className="grid gap-hurdl-6 sm:grid-cols-2 xl:grid-cols-3">
          {items.map((item) => (
            <ReviewedCard
              key={item.itemKey}
              item={item}
              productKey={product}
              restaurantKey={key}
            />
          ))}
        </div>
      )}
    </Stack>
  );
}

/**
 * One reviewed item as a card linking to its edit page: its image, name (truncated) and
 * category Badge, with a tasteful hover lift keyed to a duration token.
 */
function ReviewedCard({
  item,
  productKey,
  restaurantKey,
}: {
  readonly item: ReviewedItem;
  readonly productKey: string;
  readonly restaurantKey: string;
}): ReactElement {
  const name =
    item.displayName ?? item.extractedName + sizeSuffix(item.sizeVariant);
  const category = item.displayCategory ?? item.extractedCategory;

  return (
    <Link
      to="/products/$productKey/menus/$restaurantKey/items/$itemKey"
      params={{ productKey, restaurantKey, itemKey: item.itemKey }}
      className={TILE_LINK_CLASS}
    >
      <Card className="panel-glass h-full transition-transform duration-(--duration-hurdl-medium) ease-(--ease-hurdl-spring) group-hover:-translate-y-hurdl-1 motion-reduce:transition-none motion-reduce:group-hover:translate-y-0">
        <CardHeader>
          {item.imageUrl !== undefined ? (
            <img
              src={item.imageUrl}
              alt={name}
              className="h-40 w-full rounded-hurdl-medium border border-border object-cover"
            />
          ) : (
            <div
              aria-hidden="true"
              className="h-40 w-full rounded-hurdl-medium border border-border bg-muted"
            />
          )}
        </CardHeader>
        <CardContent>
          <Stack gap="small">
            <CardTitle className="truncate">{name}</CardTitle>
            <div>
              <Badge tone="neutral">{category}</Badge>
            </div>
          </Stack>
        </CardContent>
      </Card>
    </Link>
  );
}
